import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ConsultationStatus } from '@/shared/api/transcriptor/transcriptorApi';
import { SliceName } from '@/shared/config';

import {
  AudioIssueReason,
  TEMPLATE_DEFINITIONS,
  TemplateDefinition,
  TranscriptorConsultation,
} from '../config';
import { getErrorMessage, mergeRecordings } from '../lib';

import {
  archiveConsultation as archiveConsultationThunk,
  deleteConsultation as deleteConsultationThunk,
  fetchConsultationById,
  fetchConsultations,
  forceStopStaleConsultation,
  orderConsultationDocuments,
  reprocessConsultation,
  restoreConsultation as restoreConsultationThunk,
  startRecordingThunk,
  transferConsultation as transferConsultationThunk,
  uploadConsultation,
} from './transcriptorSlice.thunks';

const setConsultationArchived = (
  state: TranscriptorState,
  consultationId: string,
  isArchived: boolean,
) => {
  Object.values(state.consultations).forEach((consultations) => {
    const consultation = consultations.find(({ id }) => id === consultationId);
    if (consultation) {
      consultation.isArchived = isArchived;
    }
  });
};

type TranscriptorState = {
  consultations: Record<string, TranscriptorConsultation[]>; // Keyed by patientId
  loading: boolean;
  error: string | null;
  // Template definitions — static data until backend API is ready
  templates: Record<string, TemplateDefinition>;
  // Pagination info
  pagination: Record<
    string,
    {
      total: number;
      page: number;
      limit: number;
    }
  >;
  // Recording state
  recording: {
    isRecording: boolean;
    isPaused: boolean;
    isUploading: boolean;
    recordingTime: number;
    microphoneName?: string;
    consultationType: string;
    language: string;
    isProcessing: boolean;
    uploadProgress: number;
    consultationDate: string;
    patientId?: string;
    consultationId?: string;
    fragmentId?: string;
    chunkIndex: number;
    isOffline: boolean;
    lastSavedAt: string | null;
    isInterrupted: boolean;
    // No usable audio for over a minute while capture is still running.
    isSignalLost: boolean;
    audioIssue: AudioIssueReason | null;
  };
};

const initialState: TranscriptorState = {
  consultations: {},
  loading: false,
  error: null,
  templates: TEMPLATE_DEFINITIONS,
  pagination: {},
  recording: {
    isRecording: false,
    isPaused: false,
    isUploading: false,
    recordingTime: 0,
    consultationType: 'dental',
    language: 'en',
    isProcessing: false,
    uploadProgress: 0,
    consultationDate: new Date().toISOString(),
    chunkIndex: 0,
    isOffline: false,
    lastSavedAt: null,
    isInterrupted: false,
    isSignalLost: false,
    audioIssue: null,
  },
};

const transcriptorSlice = createSlice({
  name: SliceName.transcriptor,
  initialState,
  reducers: {
    setConsultations: (
      state,
      action: PayloadAction<{
        patientId: string;
        consultations: TranscriptorConsultation[];
      }>,
    ) => {
      state.consultations[action.payload.patientId] =
        action.payload.consultations;
    },
    addConsultation: (
      state,
      action: PayloadAction<{
        patientId: string;
        consultation: TranscriptorConsultation;
      }>,
    ) => {
      const { patientId, consultation } = action.payload;
      if (!state.consultations[patientId]) {
        state.consultations[patientId] = [];
      }
      state.consultations[patientId].push(consultation);
    },
    updateConsultation: (
      state,
      action: PayloadAction<{
        patientId: string;
        consultationId: string;
        updates: Partial<TranscriptorConsultation>;
      }>,
    ) => {
      const { patientId, consultationId, updates } = action.payload;
      const consultations = state.consultations[patientId];
      if (consultations) {
        const index = consultations.findIndex((c) => c.id === consultationId);
        if (index !== -1) {
          consultations[index] = { ...consultations[index], ...updates };
        }
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    // Recording actions
    startRecording: (
      state,
      action: PayloadAction<{
        microphoneName: string;
        consultationType: string;
        language: string;
        patientId: string;
      }>,
    ) => {
      state.recording.isRecording = true;
      state.recording.isPaused = false;
      state.recording.recordingTime = 0;
      state.recording.microphoneName = action.payload.microphoneName;
      state.recording.consultationType = action.payload.consultationType;
      state.recording.language = action.payload.language;
      state.recording.patientId = action.payload.patientId;
      state.recording.consultationDate = new Date().toISOString();
      state.recording.lastSavedAt = new Date().toISOString();
      state.recording.isUploading = false;
      state.recording.isProcessing = false;
      state.recording.uploadProgress = 0;
      state.recording.isSignalLost = false;
      state.recording.audioIssue = null;
    },
    pauseRecording: (state) => {
      state.recording.isPaused = true;
    },
    resumeRecording: (state) => {
      state.recording.isPaused = false;
    },
    updateRecordingTime: (state, action: PayloadAction<number>) => {
      state.recording.recordingTime = action.payload;
    },
    startUploading: (state) => {
      state.recording.isRecording = false;
      state.recording.isPaused = false;
      state.recording.isUploading = true;
    },
    stopRecording: (state) => {
      state.recording.isRecording = false;
      state.recording.isPaused = false;
      state.recording.isUploading = false;
      state.recording.isProcessing = true;
      state.recording.isInterrupted = false;
      state.recording.isSignalLost = false;
      state.recording.audioIssue = null;
    },
    updateUploadProgress: (state, action: PayloadAction<number>) => {
      state.recording.uploadProgress = action.payload;
    },
    completeRecording: (state) => {
      state.recording.isProcessing = false;
      state.recording.uploadProgress = 100;
    },
    resetRecording: (state) => {
      state.recording = initialState.recording;
    },
    setConsultationData: (
      state,
      action: PayloadAction<{ consultationId: string; fragmentId: string }>,
    ) => {
      state.recording.consultationId = action.payload.consultationId;
      state.recording.fragmentId = action.payload.fragmentId;
    },
    incrementChunkIndex: (state) => {
      state.recording.chunkIndex += 1;
    },
    resetChunkIndex: (state) => {
      state.recording.chunkIndex = 0;
    },
    setOfflineStatus: (state, action: PayloadAction<boolean>) => {
      state.recording.isOffline = action.payload;
    },
    updateLastSavedAt: (state, action: PayloadAction<string>) => {
      state.recording.lastSavedAt = action.payload;
    },
    setInterrupted: (state) => {
      state.recording.isInterrupted = true;
      state.recording.isRecording = false;
      state.recording.isPaused = false;
      state.recording.isSignalLost = false;
      state.recording.audioIssue = null;
    },
    setSignalLost: (state, action: PayloadAction<boolean>) => {
      state.recording.isSignalLost = action.payload;
    },
    setAudioIssue: (state, action: PayloadAction<AudioIssueReason | null>) => {
      state.recording.audioIssue = action.payload;
    },
    setConsultationStatus: (
      state,
      action: PayloadAction<{
        consultationId: string;
        status: ConsultationStatus;
      }>,
    ) => {
      const { consultationId, status } = action.payload;
      Object.keys(state.consultations).forEach((patientId) => {
        const consultations = state.consultations[patientId];
        const index = consultations.findIndex((c) => c.id === consultationId);
        if (index !== -1) {
          consultations[index] = {
            ...consultations[index],
            status,
          };
        }
      });
    },
  },
  extraReducers: (builder) => {
    // Fetch consultations
    builder
      .addCase(fetchConsultations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultations.fulfilled, (state, action) => {
        const { patientId, consultations, total, page, limit } = action.payload;

        state.loading = false;
        state.consultations[patientId] = consultations;
        state.pagination[patientId] = { total, page, limit };
      })
      .addCase(fetchConsultations.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload || action.error);
      });

    // Fetch single consultation
    builder
      .addCase(fetchConsultationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultationById.fulfilled, (state, action) => {
        state.loading = false;
        const consultation = action.payload;
        const patientId = consultation.patientId;

        // Update or add consultation in the list
        if (!state.consultations[patientId]) {
          state.consultations[patientId] = [consultation];
        } else {
          const index = state.consultations[patientId].findIndex(
            (c) => c.id === consultation.id,
          );
          if (index !== -1) {
            const existing = state.consultations[patientId][index];

            // Preserve local transcriptChunks when server returns empty
            // (chunks may still be uploading/processing on the server)
            const isStillProcessing = consultation.status !== 'succeeded';

            const transcriptChunks =
              consultation.transcriptChunks.length === 0 &&
              existing.transcriptChunks.length > 0 &&
              isStillProcessing
                ? existing.transcriptChunks
                : consultation.transcriptChunks;

            const recordings = mergeRecordings({
              incoming: consultation.recordings,
              existing: existing.recordings,
              isStillProcessing,
            });

            state.consultations[patientId][index] = {
              ...consultation,
              transcriptChunks,
              recordings,
            };
          } else {
            state.consultations[patientId].push(consultation);
          }
        }
      })
      .addCase(fetchConsultationById.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload || action.error);
      });

    // Start recording
    builder
      .addCase(startRecordingThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startRecordingThunk.fulfilled, (state) => {
        state.loading = false;
        // Will need to fetch the created consultation to get full details
      })
      .addCase(startRecordingThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload || action.error);
      });

    // Upload consultation
    builder
      .addCase(uploadConsultation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadConsultation.fulfilled, (state) => {
        state.loading = false;
        // Will need to fetch the created consultation to get full details
      })
      .addCase(uploadConsultation.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload || action.error);
      });

    builder
      .addCase(archiveConsultationThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveConsultationThunk.fulfilled, (state, action) => {
        state.loading = false;
        setConsultationArchived(state, action.payload.consultationId, true);
      })
      .addCase(archiveConsultationThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload || action.error);
      });

    builder
      .addCase(restoreConsultationThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreConsultationThunk.fulfilled, (state, action) => {
        state.loading = false;
        setConsultationArchived(state, action.payload.consultationId, false);
      })
      .addCase(restoreConsultationThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload || action.error);
      });

    builder
      .addCase(deleteConsultationThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConsultationThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { consultationId } = action.payload;

        Object.keys(state.consultations).forEach((patientId) => {
          const consultations = state.consultations[patientId];
          if (!consultations.some(({ id }) => id === consultationId)) {
            return;
          }

          state.consultations[patientId] = consultations.filter(
            ({ id }) => id !== consultationId,
          );
          const pagination = state.pagination[patientId];
          if (pagination) {
            pagination.total = Math.max(0, pagination.total - 1);
          }
        });
      })
      .addCase(deleteConsultationThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload || action.error);
      });

    // Transfer consultation to another doctor
    builder
      .addCase(transferConsultationThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferConsultationThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(transferConsultationThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload || action.error);
      });

    // Reprocess consultation
    builder
      .addCase(reprocessConsultation.pending, (state) => {
        state.error = null;
      })
      .addCase(reprocessConsultation.fulfilled, (state, action) => {
        const { consultationId } = action.payload;

        Object.keys(state.consultations).forEach((patientId) => {
          const consultations = state.consultations[patientId];
          const index = consultations.findIndex((c) => c.id === consultationId);

          if (index !== -1) {
            consultations[index] = {
              ...consultations[index],
              status: 'processing',
              summary: null,
              evaluation: null,
              followUp: null,
              literatureReview: null,
            };
          }
        });
      })
      .addCase(reprocessConsultation.rejected, (state, action) => {
        state.error = getErrorMessage(action.payload || action.error);
      });

    builder
      .addCase(orderConsultationDocuments.pending, (state) => {
        state.error = null;
      })
      .addCase(orderConsultationDocuments.fulfilled, (state, action) => {
        const { consultationId } = action.payload;

        Object.keys(state.consultations).forEach((patientId) => {
          const consultations = state.consultations[patientId];
          const consultationIndex = consultations.findIndex(
            (consultation) => consultation.id === consultationId,
          );

          if (consultationIndex !== -1) {
            consultations[consultationIndex] = {
              ...consultations[consultationIndex],
              status: 'processing',
            };
          }
        });
      })
      .addCase(orderConsultationDocuments.rejected, (state, action) => {
        state.error = getErrorMessage(action.payload || action.error);
      });

    // Force-stop stale consultation
    builder
      .addCase(forceStopStaleConsultation.fulfilled, (state, action) => {
        const { consultationId } = action.payload;

        Object.keys(state.consultations).forEach((patientId) => {
          const consultations = state.consultations[patientId];
          const index = consultations.findIndex((c) => c.id === consultationId);

          if (index !== -1) {
            consultations[index] = {
              ...consultations[index],
              status: 'processing',
            };
          }
        });
      })
      .addCase(forceStopStaleConsultation.rejected, (state, action) => {
        state.error = getErrorMessage(action.payload || action.error);
      });
  },
});

export const {
  setConsultations,
  addConsultation,
  updateConsultation,
  setLoading,
  setError,
  startRecording,
  pauseRecording,
  resumeRecording,
  updateRecordingTime,
  startUploading,
  stopRecording,
  updateUploadProgress,
  completeRecording,
  resetRecording,
  setConsultationData,
  incrementChunkIndex,
  resetChunkIndex,
  setOfflineStatus,
  updateLastSavedAt,
  setInterrupted,
  setSignalLost,
  setAudioIssue,
} = transcriptorSlice.actions;

export const { actions } = transcriptorSlice;

export default transcriptorSlice.reducer;
